import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ---- Types ----

export interface DailyPerformance {
  date: string;
  clicks: number;
  impressions: number;
  ctr: number;
  conversions: number;
  conversionValue: number;
}

export interface CtrImpactRow {
  product_id: string;
  product_title: string;
  optimized_at: string;
  pre_clicks: number;
  pre_impressions: number;
  pre_ctr: number;
  post_clicks: number;
  post_impressions: number;
  post_ctr: number;
  ctr_change_pct: number | null;
}

export interface CtrImpactRowV2 extends CtrImpactRow {
  optimization_number: number;
  optimization_type: string;
  /**
   * Upload-anchor: the moment products.uploaded_to_gmc_at — when this product's
   * optimized content actually went live at Google. The RPC's pre/post windows
   * are measured around THIS timestamp, not optimized_at. `optimized_at` still
   * holds the last optimization_event that preceded the upload (for display).
   */
  uploaded_at: string;
  effective_window_days: number;
  pre_ctr_ci_lower: number | null;
  pre_ctr_ci_upper: number | null;
  pre_days_with_data: number;
  post_ctr_ci_lower: number | null;
  post_ctr_ci_upper: number | null;
  post_days_with_data: number;
  z_score: number | null;
  is_significant: boolean;
  mde_relative_pct: number | null;
  pre_conversions: number;
  post_conversions: number;
  pre_conversion_value: number;
  post_conversion_value: number;
}

// ---- Utilities ----

export function fmtDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function formatMetricNumber(n: number, locale?: string): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString(locale);
}

export function formatCurrency(n: number, locale?: string): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M €`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K €`;
  return `${n.toFixed(2)} €`;
}

// ---- Shared constants & helpers ----

const PAGE_SIZE = 1000;
const MIN_IMPRESSIONS = 100; // Products below this threshold produce statistically meaningless data

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic paginator, callers cast as needed
async function fetchAllRows<T = any>(
  buildQuery: (offset: number) => PromiseLike<{ data: T[] | null }>,
): Promise<T[]> {
  const allRows: T[] = [];
  let offset = 0;
  while (true) {
    const { data: rows } = await buildQuery(offset);
    if (!rows?.length) break;
    allRows.push(...rows);
    offset += PAGE_SIZE;
    if (rows.length < PAGE_SIZE) break;
  }
  return allRows;
}

// ---- Hook: Latest performance data date for a feed ----

export function useLatestPerformanceDate(feedId: string | null) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['latest-performance-date', feedId],
    queryFn: async (): Promise<string | null> => {
      if (!feedId) return null;

      const { data } = await supabase
        .from('product_performance')
        .select('date')
        .eq('feed_id', feedId)
        .order('date', { ascending: false })
        .limit(1)
        .maybeSingle();

      return data?.date ?? null;
    },
    enabled: !!feedId && !!user?.id,
    staleTime: 120_000,
  });
}

// ---- Hook: Daily aggregated performance for a feed ----

export function useDailyPerformance(feedId: string | null, periodDays: number, latestDate?: string | null) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['daily-performance', feedId, periodDays, latestDate],
    queryFn: async (): Promise<DailyPerformance[]> => {
      if (!feedId) return [];

      // Use the latest date from DB as end date instead of "today - 2 days"
      // This ensures the chart shows actual data even when the cron hasn't run recently
      let to: Date;
      if (latestDate) {
        to = new Date(latestDate + 'T00:00:00');
      } else {
        to = new Date();
        to.setDate(to.getDate() - 2);
      }

      const from = new Date(to);
      from.setDate(from.getDate() - periodDays);

      const fromStr = fmtDate(from);
      const toStr = fmtDate(to);

      const allRows = await fetchAllRows((offset) =>
        supabase
          .from('product_performance')
          .select('product_id, date, clicks, impressions, conversions, conversion_value')
          .eq('feed_id', feedId)
          .gte('date', fromStr)
          .lte('date', toStr)
          .order('date')
          .range(offset, offset + PAGE_SIZE - 1),
      );

      // Filter: only include products with >= MIN_IMPRESSIONS total in the period
      const productImpressions = new Map<string, number>();
      for (const row of allRows) {
        productImpressions.set(row.product_id, (productImpressions.get(row.product_id) || 0) + (row.impressions || 0));
      }
      const qualifiedProducts = new Set(
        [...productImpressions.entries()].filter(([, total]) => total >= MIN_IMPRESSIONS).map(([id]) => id)
      );
      const filteredRows = allRows.filter(row => qualifiedProducts.has(row.product_id));

      // Fallback: if MIN_IMPRESSIONS filter removes ALL products, use unfiltered data
      const rowsToAggregate = filteredRows.length > 0 ? filteredRows : allRows;

      const grouped = new Map<string, { clicks: number; impressions: number; conversions: number; conversionValue: number }>();
      for (const row of rowsToAggregate) {
        const existing = grouped.get(row.date) || { clicks: 0, impressions: 0, conversions: 0, conversionValue: 0 };
        existing.clicks += row.clicks || 0;
        existing.impressions += row.impressions || 0;
        existing.conversions += row.conversions || 0;
        existing.conversionValue += Number(row.conversion_value) || 0;
        grouped.set(row.date, existing);
      }

      return Array.from(grouped.entries())
        .map(([date, data]) => ({
          date,
          clicks: data.clicks,
          impressions: data.impressions,
          ctr: data.impressions > 0 ? (data.clicks / data.impressions) * 100 : 0,
          conversions: data.conversions,
          conversionValue: data.conversionValue,
        }))
        .sort((a, b) => a.date.localeCompare(b.date));
    },
    enabled: !!feedId && !!user?.id && latestDate !== undefined,
    staleTime: 60_000,
  });
}

// ---- Hook: Upload dates for chart reference lines ----
// Returns dates (YYYY-MM-DD) when products were actually uploaded to Google Merchant Center.
// We use upload completion (NOT optimization timestamp) because changes only become visible
// to Google after the upload — the chart reference lines must mark the moment perf can shift.

export function useUploadDates(feedId: string | null, periodDays: number, latestDate?: string | null) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['upload-dates', feedId, periodDays, latestDate],
    queryFn: async (): Promise<string[]> => {
      if (!feedId) return [];

      // Match the date range used by useDailyPerformance
      let to: Date;
      if (latestDate) {
        to = new Date(latestDate + 'T00:00:00');
      } else {
        to = new Date();
        to.setDate(to.getDate() - 2);
      }
      const from = new Date(to);
      from.setDate(from.getDate() - periodDays);

      const { data } = await supabase
        .from('upload_jobs')
        .select('completed_at')
        .eq('feed_id', feedId)
        .eq('status', 'completed')
        .gte('completed_at', from.toISOString())
        .order('completed_at', { ascending: true });

      if (!data?.length) return [];
      return [...new Set(
        data
          .filter(e => e.completed_at)
          .map(e => (e.completed_at as string).split('T')[0])
      )];
    },
    enabled: !!feedId && !!user?.id,
    staleTime: 60_000,
  });
}

// ---- Hook: CTR impact v2 (multi-optimization support) ----

/**
 * Seiten-Deckel von `get_optimization_impact_v2`. Exportiert, weil jeder, der
 * aus dieser Liste eine ANZAHL ableitet, wissen muss, ob sie abgeschnitten ist:
 * kommen genau so viele Zeilen zurueck, ist unbekannt, ob es mehr gibt. Ohne
 * diesen Vergleich stand am Lift-Kurven-Kopf eine stille Falschzahl.
 */
export const CTR_IMPACT_PAGE_LIMIT = 1000;

export function useCtrImpactV2(
  feedId: string | null,
  options?: { windowDays?: number; limit?: number }
) {
  const { user } = useAuth();
  const windowDays = options?.windowDays ?? 14;
  const limit = options?.limit ?? CTR_IMPACT_PAGE_LIMIT;

  return useQuery({
    queryKey: ['ctr-impact-v2', feedId, user?.id, windowDays, limit],
    queryFn: async (): Promise<CtrImpactRowV2[]> => {
      if (!feedId || !user?.id) return [];
      const { data, error } = await supabase.rpc('get_optimization_impact_v2', {
        p_feed_id: feedId,
        p_user_id: user.id,
        p_window_days: windowDays,
        p_limit: limit,
      });
      if (error) throw error;
      return (data || []) as CtrImpactRowV2[];
    },
    enabled: !!feedId && !!user?.id,
    staleTime: 120_000,
  });
}

// ---- Hook: Feed performance for impact card (home widget) ----

export interface FeedPerformanceData {
  totalClicks: number;
  totalImpressions: number;
  avgCtr: number;
  prevClicks: number;
  prevImpressions: number;
  prevCtr: number;
  daysSinceUpload: number | null;
  hasData: boolean;
  hasUpload: boolean;
  ctrImpactAvg: number | null;
  ctrImpactCount: number;
  ctrImproved: number;
  topImprovements: Array<{ title: string; change: number }>;
}

export function useFeedPerformance(feedId: string, days: number) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['feed-performance-impact', feedId, user?.id, days],
    queryFn: async (): Promise<FeedPerformanceData | null> => {
      if (!feedId || !user?.id) return null;

      const { data: uploadJob } = await supabase
        .from('upload_jobs')
        .select('completed_at')
        .eq('feed_id', feedId)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const base: FeedPerformanceData = {
        totalClicks: 0, totalImpressions: 0, avgCtr: 0,
        prevClicks: 0, prevImpressions: 0, prevCtr: 0,
        ctrImpactAvg: null, ctrImpactCount: 0, ctrImproved: 0,
        topImprovements: [],
        daysSinceUpload: null, hasData: false, hasUpload: false,
      };

      if (!uploadJob?.completed_at) {
        return base;
      }

      const uploadDate = new Date(uploadJob.completed_at);
      const now = new Date();
      const daysSinceUpload = Math.floor((now.getTime() - uploadDate.getTime()) / (1000 * 60 * 60 * 24));

      // Query the latest performance date to anchor the range on actual data
      const { data: latestRow } = await supabase
        .from('product_performance')
        .select('date')
        .eq('feed_id', feedId)
        .order('date', { ascending: false })
        .limit(1)
        .maybeSingle();

      const recentTo = latestRow?.date
        ? new Date(latestRow.date + 'T00:00:00')
        : (() => { const d = new Date(); d.setDate(d.getDate() - 2); return d; })();

      const recentFrom = new Date(recentTo);
      recentFrom.setDate(recentFrom.getDate() - days);

      const recentFromStr = fmtDate(recentFrom);
      const recentToStr = fmtDate(recentTo);
      const prevFromStr = fmtDate(new Date(recentFrom.getTime() - days * 86400000));

      const [recentPerfRaw, prevPerfRaw, ctrResult] = await Promise.all([
        fetchAllRows((offset) =>
          supabase
            .from('product_performance')
            .select('product_id, clicks, impressions')
            .eq('feed_id', feedId)
            .gte('date', recentFromStr)
            .lte('date', recentToStr)
            .range(offset, offset + PAGE_SIZE - 1),
        ),
        fetchAllRows((offset) =>
          supabase
            .from('product_performance')
            .select('product_id, clicks, impressions')
            .eq('feed_id', feedId)
            .gte('date', prevFromStr)
            .lt('date', recentFromStr)
            .range(offset, offset + PAGE_SIZE - 1),
        ),
        supabase.rpc('get_optimization_ctr_impact', {
          p_feed_id: feedId,
          p_user_id: user.id,
        }),
      ]);

      // Filter: only include products with >= MIN_IMPRESSIONS total in recent period
      const recentProductImpressions = new Map<string, number>();
      for (const r of recentPerfRaw) {
        recentProductImpressions.set(r.product_id, (recentProductImpressions.get(r.product_id) || 0) + (r.impressions || 0));
      }
      const qualifiedProducts = new Set(
        [...recentProductImpressions.entries()].filter(([, total]) => total >= MIN_IMPRESSIONS).map(([id]) => id)
      );
      const recentPerf = recentPerfRaw.filter(r => qualifiedProducts.has(r.product_id));
      const prevPerf = prevPerfRaw.filter(r => qualifiedProducts.has(r.product_id));

      if (!recentPerf.length) {
        return { ...base, daysSinceUpload, hasData: false, hasUpload: true };
      }

      const totalClicks = recentPerf.reduce((s, r) => s + (r.clicks || 0), 0);
      const totalImpressions = recentPerf.reduce((s, r) => s + (r.impressions || 0), 0);
      const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

      const prevClicks = prevPerf.reduce((s, r) => s + (r.clicks || 0), 0);
      const prevImpressions = prevPerf.reduce((s, r) => s + (r.impressions || 0), 0);
      const prevCtr = prevImpressions > 0 ? (prevClicks / prevImpressions) * 100 : 0;

      let ctrImpactAvg: number | null = null;
      let ctrImpactCount = 0;
      let ctrImproved = 0;
      let topImprovements: Array<{ title: string; change: number }> = [];

      if (ctrResult.data?.length) {
        const withBoth = (ctrResult.data as CtrImpactRow[]).filter(
          r => r.pre_impressions >= MIN_IMPRESSIONS && r.post_impressions >= MIN_IMPRESSIONS && r.ctr_change_pct !== null
        );
        ctrImpactCount = withBoth.length;
        if (ctrImpactCount > 0) {
          ctrImpactAvg = withBoth.reduce((s, r) => s + Number(r.ctr_change_pct), 0) / ctrImpactCount;
          ctrImproved = withBoth.filter(r => Number(r.ctr_change_pct) > 0).length;
          topImprovements = withBoth
            .filter(r => Number(r.ctr_change_pct) > 0)
            .sort((a, b) => Number(b.ctr_change_pct) - Number(a.ctr_change_pct))
            .slice(0, 3)
            .map(r => ({ title: r.product_title || 'Untitled', change: Number(r.ctr_change_pct) }));
        }
      }

      return {
        totalClicks, totalImpressions, avgCtr,
        prevClicks, prevImpressions, prevCtr,
        daysSinceUpload, hasData: true, hasUpload: true,
        ctrImpactAvg, ctrImpactCount, ctrImproved, topImprovements,
      };
    },
    enabled: !!feedId && !!user?.id,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

// ---- Hook: Aggregated Lift Curve for significant products ----

export interface LiftCurvePoint {
  relative_day: number;
  clicks: number;
  impressions: number;
  ctr_pct: number | null;
  product_count: number;
}

export function useLiftCurve(feedId: string | null, windowDays: number) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['lift-curve', feedId, user?.id, windowDays],
    queryFn: async (): Promise<LiftCurvePoint[]> => {
      if (!feedId || !user?.id) return [];
      const { data, error } = await supabase.rpc('get_lift_curve', {
        p_feed_id: feedId,
        p_user_id: user.id,
        p_window_days: windowDays,
      });
      if (error) throw error;
      return (data ?? []) as LiftCurvePoint[];
    },
    enabled: !!feedId && !!user?.id,
    staleTime: 120_000,
  });
}

// ---- Hook: Von Google fuer Shopping Ads abgelehnte Produkte eines Feeds ----

/**
 * Eine Zeile aus `products` mit Googles Auslieferungs-Urteil (Serving-Gate,
 * Migration `20260824090000`). `shopping_ads_disapproved_at` nicht null heisst:
 * beim juengsten taeglichen Abruf (`pull-gmc-disapprovals`, 06:45 UTC) hat
 * Google dieses Produkt fuer Shopping Ads abgelehnt.
 */
export interface DisapprovedProductRow {
  id: string;
  shopping_ads_disapproved_at: string | null;
  shopping_ads_issues: string[] | null;
}

/**
 * `product_id` der Impact-RPC ist `products.id` (siehe
 * `20260518120000_impact_v4_upload_anchored.sql`, `p.id AS product_id`) — der
 * Schluessel dieser Map passt also direkt auf eine `CtrImpactRowV2`.
 *
 * Der Wert ist die Issue-Liste, moeglicherweise leer: `shopping_ads_issues`
 * darf null sein, und "abgelehnt, Grund unbekannt" ist ein anderer Zustand als
 * "nicht abgelehnt". Die Unterscheidung traegt hier die Existenz des
 * Schluessels, nicht der Inhalt des Arrays.
 */
export function buildDisapprovedMap(rows: DisapprovedProductRow[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const row of rows) {
    if (!row?.id) continue;
    map.set(row.id, row.shopping_ads_issues ?? []);
  }
  return map;
}

/**
 * Laedt die abgelehnten Produkte EINES Feeds, getragen vom Partial-Index
 * `idx_products_shopping_ads_disapproved (feed_id) WHERE
 * shopping_ads_disapproved_at IS NOT NULL`.
 *
 * Bewusst feedweit statt `.in('id', <bis zu 1000 UUIDs>)`: PostgREST legt einen
 * `in`-Filter in den URL-Query-String (~37 Byte je UUID), das reisst bei grossen
 * Mengen die URL-Grenze von Kong — dieselbe Falle, die `workbench_bulk_reopen`
 * in eine RPC gezwungen hat. Die geflaggte Menge je Feed ist klein (groesstes
 * Konto 463 von 3.777 Zeilen, gemessen 26.08.2026).
 *
 * Paginiert trotzdem: PostgREST kappt bei `db-max-rows` = 1000 Zeilen, und eine
 * still gekappte Liste wuerde eine zu kleine Anzahl in die Kopfzeile schreiben.
 * `order('id')` ist dabei nicht Kosmetik — ohne stabile Sortierung sind die
 * Grenzen eines `range()` nicht dicht, Zeilen koennen zwischen zwei Seiten
 * ausfallen (Lehre aus `pull-gmc-disapprovals`).
 */
export function useDisapprovedProducts(feedId: string | null) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['disapproved-products', feedId],
    queryFn: async (): Promise<DisapprovedProductRow[]> =>
      fetchAllRows<DisapprovedProductRow>((offset) =>
        supabase
          .from('products')
          .select('id, shopping_ads_disapproved_at, shopping_ads_issues')
          .eq('feed_id', feedId as string)
          .not('shopping_ads_disapproved_at', 'is', null)
          .order('id')
          .range(offset, offset + PAGE_SIZE - 1) as unknown as PromiseLike<{ data: DisapprovedProductRow[] | null }>,
      ),
    enabled: !!feedId && !!user?.id,
    staleTime: 300_000,
  });
}
